import axios from "axios";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import { Endpoints } from "@/utils/api/endpoints";
import { getSessionManager, getEventBus } from "./eventIntegration";
import { captureScopedScreenshot } from "./screenshotCapture";
import { getEnvironmentContext } from "@/utils/debug";
import { updateIntercomWithDebugSession } from "@/utils/intercom";
import { emitDebugEvent } from "./eventIntegration";
import { useAuthStore } from "@utils/stores/authStore";
import type { DebugEvent, DebugTimeline } from "@smartwiz/debug-events";
import {
  MAX_RETRIES,
  INITIAL_RETRY_DELAY_MS,
  EXTENSION_DEBUG_DUMP_TIMEOUT_MS,
} from "./constants";

/**
 * Request extension debug dump via message passing
 */
async function requestExtensionDebugDump(
  sessionId: string
): Promise<DebugEvent[]> {
  return new Promise((resolve) => {
    let timeout: number;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.data?.type !== "DEBUG_SESSION_DUMP" ||
        event.data?.payload?.sessionId !== sessionId
      ) {
        return;
      }

      const events = event.data.payload?.events || [];
      clearTimeout(timeout);
      window.removeEventListener("message", handleMessage);
      resolve(events);
    };

    // Set timeout
    timeout = window.setTimeout(() => {
      window.removeEventListener("message", handleMessage);
      resolve([]);
    }, EXTENSION_DEBUG_DUMP_TIMEOUT_MS);

    window.addEventListener("message", handleMessage);

    // Send request to extension background
    window.postMessage(
      {
        type: "DEBUG_SESSION_START",
        sessionId,
      },
      window.location.origin
    );
  });
}

/**
 * Request extension timeline dump via message passing
 */
async function requestExtensionTimelineDump(
  sessionId: string
): Promise<DebugTimeline | null> {
  return new Promise((resolve) => {
    let timeout: number;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.data?.type !== "DEBUG_TIMELINE_DUMP_RESULT" ||
        event.data?.payload?.sessionId !== sessionId
      ) {
        return;
      }

      const filmstrip = event.data.payload?.filmstrip || null;
      clearTimeout(timeout);
      window.removeEventListener("message", handleMessage);
      resolve(filmstrip);
    };

    timeout = window.setTimeout(() => {
      window.removeEventListener("message", handleMessage);
      resolve(null);
    }, EXTENSION_DEBUG_DUMP_TIMEOUT_MS);

    window.addEventListener("message", handleMessage);

    window.postMessage(
      {
        type: "DEBUG_TIMELINE_DUMP",
        sessionId,
      },
      window.location.origin
    );
  });
}

// Screenshot is uploaded as part of the session creation request
// No separate upload function needed

/**
 * Create debug session via API
 */
async function createDebugSessionAPI(payload: {
  sessionId: string;
  events: DebugEvent[];
  filmstrip?: DebugTimeline | null;
  screenshot?: string | null;
  environment: any;
}): Promise<{ debug_session_id: string; support_summary?: string } | null> {
  try {
    const requestPayload = {
      session_id: payload.sessionId,
      events: payload.events,
      filmstrip: payload.filmstrip || null,
      screenshot: payload.screenshot || null,
      environment_metadata: payload.environment,
    };

    const response = await axios.post(
      `${backend_url}/${Endpoints.support.debugSession}`,
      requestPayload,
      AXIOS_CONFIG
    );

    const debugSessionId = response.data.content?.debug_session_id;
    if (!debugSessionId) {
      return null;
    }

    return {
      debug_session_id: debugSessionId,
      support_summary: response.data.content?.support_summary,
    };
  } catch (error: any) {
    throw error;
  }
}

/**
 * Create a debug session when Intercom support is opened.
 *
 * Flow (Screenshot is Optional):
 * 1. Start session (generate UUID)
 * 2. Start visual timeline capture in the extension
 * 3. Attempt screenshot capture (scoped) - may return null
 * 4. Get event history from bus
 * 5. Request extension events and filmstrip (in parallel)
 * 6. If screenshot exists: Include in API call
 * 7. Create debug session via API (with or without S3 key)
 * 8. Update Intercom with debug_session_id
 * 9. Handle errors gracefully (don't block user)
 *
 * Key Principle: Session Creation Never Blocks on Screenshot
 * - Screenshot capture failure → continue without screenshot
 * - Screenshot upload failure → continue without screenshot
 * - Session is created successfully even if screenshot is missing
 * - Support can still see event timeline and context
 *
 * Edge Case Handling:
 * - Intercom reopen: Guard prevents duplicate sessions per open
 * - Network failure: Retries up to 2 times with exponential backoff
 * - Screenshot failure: Session created without screenshot (optional)
 */
export async function createDebugSession(): Promise<string | null> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const sessionManager = getSessionManager();
      const eventBus = getEventBus();

      // 1. Start session
      const sessionId = sessionManager.startSession();

      // 2. Start timeline capture in the extension
      window.postMessage(
        {
          type: "DEBUG_TIMELINE_START",
          sessionId,
        },
        window.location.origin
      );

      // 3. Attempt screenshot (non-blocking)
      let screenshotBase64: string | null = null;
      try {
        console.log(
          "[Screenshot] 📸 Starting screenshot capture in debug session..."
        );
        screenshotBase64 = await captureScopedScreenshot();
        console.log(
          "[Screenshot] ✅ Screenshot capture completed in debug session",
          {
            hasScreenshot: !!screenshotBase64,
            screenshotLength: screenshotBase64?.length || 0,
          }
        );
      } catch (captureError) {
        console.warn(
          "[Screenshot] ⚠️ Screenshot capture failed in debug session, continuing without screenshot",
          {
            error: captureError,
          }
        );
        // Continue - screenshot is optional
      }

      // 4. Get event history (always available)
      const eventHistory = eventBus.getHistory();

      // 5. Request extension events + timeline (may timeout - that's OK)
      let extensionEvents: DebugEvent[] = [];
      let filmstrip: DebugTimeline | null = null;
      try {
        const [extensionDump, timelineDump] = await Promise.all([
          requestExtensionDebugDump(sessionId).catch(() => []),
          requestExtensionTimelineDump(sessionId).catch(() => null),
        ]);
        extensionEvents = extensionDump;
        filmstrip = timelineDump;
      } finally {
        window.postMessage(
          {
            type: "DEBUG_TIMELINE_STOP",
            sessionId,
          },
          window.location.origin
        );
      }

      // 6. Get environment context
      const environment = getEnvironmentContext();

      // 7. Create session (with or without screenshot)
      const allEvents = [...eventHistory.all, ...extensionEvents];

      const sessionResponse = await createDebugSessionAPI({
        sessionId,
        events: allEvents,
        filmstrip,
        screenshot: screenshotBase64,
        environment: {
          browser: environment.browser,
          browserVersion: environment.browserVersion || "",
          operatingSystem: environment.operatingSystem,
          deviceType: environment.deviceType,
          userAgent: environment.userAgent,
        },
      });

      // 8. Update Intercom with debug session ID and summary
      if (sessionResponse?.debug_session_id) {
        updateIntercomWithDebugSession(
          sessionResponse.debug_session_id,
          sessionResponse.support_summary
        );

        // Emit debug event for session creation
        try {
          const { user } = useAuthStore.getState();
          emitDebugEvent("support", "DEBUG_SESSION_CREATED", "web", {
            debugSessionId: sessionResponse.debug_session_id,
            eventCount: allEvents.length.toString(),
            userId: user?.id || "unknown",
            hasScreenshot: !!screenshotBase64,
          });
        } catch (err) {
          // Ignore errors
        }
      }

      // 9. Flush session (mark as flushed)
      sessionManager.flush(sessionId, allEvents, {
        browser: environment.browser,
        browserVersion: environment.browserVersion || "",
        operatingSystem: environment.operatingSystem,
        deviceType: environment.deviceType,
      });

      // 10. Clear event buffers and reset session for next session
      eventBus.clear();
      sessionManager.reset();
      sessionManager.startSession(); // Start new session immediately

      return sessionResponse?.debug_session_id || null;
    } catch (error) {
      // Retry logic for network failures
      if (attempt < MAX_RETRIES) {
        const retryDelay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue; // Retry
      }

      // Final failure after all retries
      return null;
    }
  }

  return null;
}
